/*! Copyright (C) Microsoft. All rights reserved. */

import Support from "../../Support/Support";
import { updateFeedbackTextAreaPlaceholder } from "./FeedbackTextAreaPlaceholder";

declare const occe: Support & { Controls: { Feedback: FeedbackControl } };

interface FeedbackControl {
	initializeControl(): void;
	attachFeedbackAboveUniversalFooter(): void;
	makeFeedbackSticky(): void;
	hasUserDisabledFeedbackStickiness(): boolean;
	hasUserSeenOneFourthOfArticle(): boolean;
	updateFeedbackStickiness(): void;
	isUniversalFooterVisible(): boolean;
	setExtendedFeedbackHeight(): void;
	userHasDisabledFeedbackStickinessCookieName: string;
	setUserHasDisabledFeedbackStickiness(value: boolean): void;
	setUniversalFooterInViewport(value: boolean): void;
	setWindowTooTightForStickiness(value: boolean): void;
	setArticleTooShortForStickiness(value: boolean): void;
	setUserHasSeenOneFourthOfArticle(value: boolean): void;
}

interface Analytics {
	captureContentUpdate(tag: AwaTag): void;
	capturePageAction(element: Element, options: PageActionOptions, extra: null): void;
}

interface OneDS {
	Behavior: Record<string, string | number>;
}

interface AwaTag {
	actionType?: string;
	behavior?: number;
	content?: Record<string, unknown>;
	[key: string]: unknown;
}

interface PageActionOptions {
	behavior: number;
	actionType: string;
	contentTags: Record<string, unknown>;
}

interface TelemetryTags {
	"ms.ea_action": string;
	"ms.svq": string;
	"ms.svv": string;
	"ms.interactiontype"?: string;
	"ms.sva"?: string;
	"ms.infoHelpful"?: string | null;
	"ms.articleExp"?: string;
	"ms.starRating"?: string | number;
	"wcs.cot"?: string;
	"wcs.et"?: string;
	"wcs.cn"?: string;
	[key: string]: unknown;
}

declare global {
	interface Window {
		Top: Window & { analytics?: Analytics; oneDS?: OneDS };
		Analytics?: Analytics;
		OneDS?: OneDS;
	}
}

const $feedbackRendering = {
	fullText: $("#extendedFeedback"),
	formYesNo: $("#extendedFeedbackForm"),
	wrapper: $("#modernFeedback"),
	starDescription: $("#starRatingDescription"),
	disableButton: $("#disableStickyFeedbackButton"),
	starRating: $("#extendedFeedbackStarRating"),
	section: $("section.feedbackSection"),
};

const isEnableStarRating =
	($feedbackRendering.wrapper.data("enableStarRating") || "").toLowerCase() === "true";
const starRatingQuestion =
	$feedbackRendering.starRating.data("starRatingQuestion") as string;

function buildTags(overrides: Partial<TelemetryTags>): TelemetryTags {
	return Object.assign(
		{ "ms.ea_action": "Comment", "ms.svq": "2", "ms.svv": "" },
		overrides
	);
}

function setAlert($elem: JQuery): void {
	$elem.attr({
		"aria-label": ($elem[0] as HTMLElement).innerText.trim(),
		role: "alert",
	});
}

function logTelemetry(awaTag: AwaTag, behavior: number): void {
	if (typeof window.top?.analytics === "object") {
		awaTag.behavior = window.top.oneDS?.Behavior[behavior as unknown as string];
		window.top.analytics.captureContentUpdate(awaTag);
	}
}

function sendButtonAnalytics(tags: TelemetryTags, $control: JQuery): void {
	if (typeof window.analytics !== "object") return;
	window.analytics.capturePageAction(
		$control[0],
		{
			behavior: typeof window.oneDS === "object" ? window.oneDS.Behavior.VOTE : 140,
			actionType: "CL",
			contentTags: {
				sat: tags["ms.sva"],
				fbnm: "Feedback",
				fbid: 1,
				areaName: "Feedback",
			},
		},
		null
	);
}

function sendFormAnalytics(tags: TelemetryTags): void {
	if (typeof window.analytics !== "object") return;
	const contentTags: Record<string, unknown> = {
		contentId: "buttonFeedbackVerbatimSubmit",
		fbnm: "Feedback",
		fbid: 2,
		areaName: "Feedback",
		vtbm: tags["ms.svv"],
		infoHelpful: tags["ms.infoHelpful"],
		articleExperience: tags["ms.articleExp"],
	};
	if (isEnableStarRating) {
		contentTags.starRating = tags["ms.starRating"];
		contentTags.starRatingQuestion = starRatingQuestion;
	}
	window.analytics.capturePageAction(
		$('[name="feedbackText"]')[0],
		{
			behavior: typeof window.oneDS === "object" ? window.oneDS.Behavior.SURVEYCOMPLETE : 142,
			actionType: "CL",
			contentTags,
		},
		null
	);
}

function switchToFullText($parent: JQuery, userSelectionIsInfoHelpful: string | null): void {
	const $feedbackYesNoSelectionElem = $parent.find(".feedbackBegin");
	$feedbackYesNoSelectionElem.removeClass("d-block d-sm-flex").hide();
	$parent.find(".feedbackReply").removeClass("hidden");
	$feedbackRendering.wrapper.addClass("extendedFeedbackFullTextIsOpen");

	logTelemetry(
		{
			actionType: "A",
			content: {
				contentId: "userSeenVbtmFeedbackArea",
				fbnm: "Feedback",
				fbid: 2,
				areaName: "Feedback",
			},
		},
		145
	);

	occe.Controls.Feedback.setExtendedFeedbackHeight();
	$(".prrFeedbackWrapper").css("height", "inherit");

	const focusTarget = isEnableStarRating
		? "#rating_0"
		: userSelectionIsInfoHelpful === "buttonYes"
			? "#helpful_0"
			: "#notHelpful_0";

	$parent.find(focusTarget).focus();

	const $header = $("#extendedFeedbackHeader");
	$header.get(0).scrollIntoView();
	setAlert($header);
	$parent.find("#buttonFeedbackVerbatimSubmit")[0].scrollIntoView(false);
}

function switchToThankYou($parent: JQuery): void {
	$parent.find("#feedbackThankYouMessage").removeClass("hidden");
	$feedbackRendering.wrapper
		.removeClass("extendedFeedbackFullTextIsOpen")
		.css("height", "auto");
	$feedbackRendering.fullText.addClass("hidden");
	$(".prrColumnWrapper").css("height", "inherit");

	const $thankYouMessage = $parent.find(".feedbackThankYou");
	setAlert($thankYouMessage);
	$thankYouMessage.focus();
}

function renderStars(activatedRatingIndex: number | null): void {
	for (let i = 0; i < 5; i++) {
		$("#rating_" + i).toggleClass(
			"glyph-favorite-star-fill starRatingFillBlue",
			activatedRatingIndex !== null && i <= activatedRatingIndex
		);
	}
}

function visibleHeight(selector: string): number {
	const $el = $(selector);
	return $el.css("visibility") === "visible" ? ($el.height() as number) : 0;
}

export function initFeedbackForm(): void {
	let activatedStarRatingValue: number | null = null;
	let activatedStarRatingLabel: string | null = null;
	let userSelectionIsInfoHelpful: string | null = null;
	let checkBoxSelected = false;
	let starRatingSelected = false;
	let verbatimEntered = false;

	const $starCheckboxSpans = $(".translationRatingStar, .checkboxTick");
	const $starCheckboxInputs = $(".translationRatingStar, .articleExperienceOptionsCheckbox");
	const $feedbackTextArea = $(".feedbackTextArea");
	const feedbackTextAreaPlaceholder = $feedbackTextArea.attr("placeholder") as string;

	function updateSubmitButton(): void {
		$("#buttonFeedbackVerbatimSubmit").prop(
			"disabled",
			!starRatingSelected && !checkBoxSelected && !verbatimEntered
		);
	}

	function onStarMouseLeave(): void {
		renderStars(activatedStarRatingValue);
		if (activatedStarRatingLabel !== null) {
			$feedbackRendering.starDescription.text(activatedStarRatingLabel).removeClass("hidden");
		} else {
			$feedbackRendering.starDescription.addClass("hidden");
		}
	}

	$starCheckboxSpans.on("keypress", function (e: JQuery.KeyPressEvent) {
		if (e.keyCode === 13 || e.keyCode === 32) {
			const $this = $(this);
			($this.hasClass("checkboxTick")
				? $this.siblings(".articleExperienceOptionsCheckbox")
				: $this
			)[0].click();
			e.preventDefault();
		}
	});

	$starCheckboxInputs.on("click", function () {
		const $this = $(this);
		if ($this.hasClass("articleExperienceOptionsCheckbox")) {
			checkBoxSelected = $(".articleExperienceOptionsCheckbox:checked").length > 0;
			$this
				.siblings(".checkboxTick")
				.attr("aria-checked", String(($this[0] as HTMLInputElement).checked));
		} else {
			starRatingSelected = true;
		}
		updateSubmitButton();
	});

	$feedbackTextArea.on("input", function () {
		const textArea = this as HTMLTextAreaElement;
		verbatimEntered = textArea.value.length > 0;
		updateFeedbackTextAreaPlaceholder(textArea, feedbackTextAreaPlaceholder);
		updateSubmitButton();
	});

	$(".smartFeedbackForm")
		.on("click", ".feedbackSelection", function () {
			const $this = $(this);
			userSelectionIsInfoHelpful = $this.attr("data-bi-id") ?? null;

			const isYes = $this.attr("name") === "buttonYes";
			const tags = buildTags({
				"ms.interactiontype": occe.constants.wedcs.interactionType.completedFinalStepInSeries,
				"ms.ea_action": "FeedbackControlRate",
				"ms.svq": "1",
				"wcs.cot": "1",
				"wcs.et": "2",
				"wcs.cn": ($this[0] as HTMLElement).innerText || "",
				"ms.sva": isYes ? "100" : "0",
			});

			$feedbackRendering.section
				.find(isYes ? ".feedbackHeaderIfArticleHelpful" : ".feedbackHeaderIfArticleNotHelpful")
				.removeClass("hidden");

			sendButtonAnalytics(tags, $this);
			switchToFullText($feedbackRendering.section, userSelectionIsInfoHelpful);
		})
		.on("submit", function (e: JQuery.SubmitEvent) {
			e.preventDefault();
			$(this).addClass("hidden");

			const articleExperience = $("input:checkbox:checked")
				.map(function () { return $(this).val() as string; })
				.get();

			const tags = buildTags({
				"ms.interactiontype": occe.constants.wedcs.interactionType.formSubmit,
				"ms.svv": $feedbackRendering.section.find(".feedbackTextArea").val() as string,
				"ms.infoHelpful": userSelectionIsInfoHelpful,
				"ms.articleExp": articleExperience.length ? articleExperience.toString() : "",
			});

			if (isEnableStarRating) {
				tags["ms.starRating"] =
					activatedStarRatingValue !== null ? activatedStarRatingValue + 1 : "";
			}

			switchToThankYou($feedbackRendering.section);
			sendFormAnalytics(tags);
		});

	$feedbackRendering.disableButton.on("click", function () {
		const isVisible = $feedbackRendering.formYesNo.css("visibility") === "visible";
		const isOpen = isVisible && !$feedbackRendering.fullText.hasClass("hidden");
		const thankYouOn = !$("#feedbackThankYouMessage").hasClass("hidden");

		if (
			!isVisible ||
			(isVisible && $(".feedbackBegin").is(":visible")) ||
			(isVisible && thankYouOn)
		) {
			occe.SetCookie(
				occe.Controls.Feedback.userHasDisabledFeedbackStickinessCookieName,
				"true"
			);
			occe.Controls.Feedback.setUserHasDisabledFeedbackStickiness(true);
			occe.Controls.Feedback.attachFeedbackAboveUniversalFooter();
		} else if (isOpen) {
			$(".smartFeedbackForm").addClass("hidden");
			$feedbackRendering.disableButton.removeClass("hidden");
			switchToThankYou($feedbackRendering.section);
			sendButtonAnalytics(buildTags({ "ms.interactiontype": '12'}), $(this));
		}
	});

	$(".translationRatingStar")
		.on("click", function () {
			const $this = $(this);
			activatedStarRatingValue = $this.data("startindex") as number;
			activatedStarRatingLabel = $this.data("stardescription") as string;
			renderStars(activatedStarRatingValue);
			$feedbackRendering.starDescription
				.text(activatedStarRatingLabel)
				.removeClass("hidden");
			$(".translationRatingStar").off("mouseleave");
		})
		.on("mouseenter", function () {
			const $this = $(this);
			$(".translationRatingStar").on("mouseleave", onStarMouseLeave);
			renderStars($this.data("startindex") as number);
			$feedbackRendering.starDescription
				.text($this.data("stardescription") as string)
				.removeClass("hidden");
		});
}

export function initFeedbackControl(): void {
	occe.Controls = occe.Controls || ({} as typeof occe.Controls);
	occe.Controls.Feedback = (function (): FeedbackControl {
		let $universalFooter: JQuery;
		let $articleElement: JQuery;
		let $feedbackSection: JQuery;
		const $window = $(window);

		const cookieName = "userHasDisabledFeedbackStickiness";
		let userDisabled: boolean;
		let windowTooTight: boolean;
		let articleTooShort: boolean;
		let seenOneFourth: boolean;
		let footerInViewport: boolean;

		function init(): void {
			$universalFooter = $("#footerArea");
			$articleElement = $("#ocArticle");
			$feedbackSection = $("#feedbackSection");

			initStickiness();

			$window.on("scroll resize", function () {
				seenOneFourth = hasSeenOneFourth();
				windowTooTight = isWindowTooTight();
				footerInViewport = isFooterInViewport();
				if ($("#livefyre").length) {
					articleTooShort = isArticleTooShort();
				}
				updateStickiness();
			});

			$articleElement.on("click", function () {
				footerInViewport = isFooterInViewport();
				updateStickiness();
			});
		}

		function initStickiness(): void {
			userDisabled = occe.GetCookie(cookieName) === "true";
			windowTooTight = isWindowTooTight();
			articleTooShort = isArticleTooShort();
			seenOneFourth = hasSeenOneFourth();
			footerInViewport = isFooterInViewport();
		}

		function isWindowTooTight(): boolean {
			return occe.windowIsTabletSizeOrSmaller();
		}

		function isArticleTooShort(): boolean {
			return $universalFooter.offset()!.top - ($window.height() as number) <= 200;
		}

		function isFooterInViewport(): boolean {
			return (
				($window.scrollTop() as number) + ($window.height() as number) >
				$universalFooter.offset()!.top
			);
		}

		function hasSeenOneFourth(): boolean {
			if (seenOneFourth) return true;
			return (
				($window.scrollTop() as number) >
				$articleElement.offset()!.top + ($articleElement.height() as number) / 4
			);
		}

		function canBeSticky(): boolean {
			return !footerInViewport && !userDisabled && !windowTooTight && seenOneFourth && !articleTooShort;
		}

		function setExtendedFeedbackHeight(): void {
			const starRatingHeight = visibleHeight("#extendedFeedbackStarRating");
			const total =
				visibleHeight("#extendedFeedbackHeader") +
				starRatingHeight +
				visibleHeight("#extendedFeedbackArticleExperience") +
				visibleHeight("#extendedFeedbackVerbatim") +
				visibleHeight("#extendedFeedbackFormSubmit") +
				visibleHeight("#privacyStatement") +
				(starRatingHeight === 0 ? 90 : 110);
			$(".extendedFeedbackFullTextIsOpen").css("height", total + "px");
		}

		function makeSticky(): void {
			$feedbackSection.addClass("stickyFeedback");
			$feedbackRendering.disableButton.css("top", "0").removeClass("hidden");
		}

		function attachAboveFooter(): void {
			$feedbackSection.removeClass("stickyFeedback");
			const extendedOpen =
				$feedbackRendering.formYesNo.css("visibility") === "visible" &&
				!$feedbackRendering.fullText.hasClass("hidden");
			if (extendedOpen) {
				$feedbackRendering.disableButton
					.removeClass("hidden")
					.css("top", $feedbackRendering.wrapper.position().top);
			} else {
				$feedbackRendering.disableButton.addClass("hidden");
			}
		}

		function updateStickiness(): void {
			if (canBeSticky()) {
				makeSticky();
			} else {
				if (
					$feedbackRendering.wrapper.hasClass("extendedFeedbackFullTextIsOpen") &&
					$feedbackRendering.fullText.css("visibility") === "visible"
				) {
					setExtendedFeedbackHeight();
				}
				attachAboveFooter();
			}
		}

		return {
			initializeControl: init,
			attachFeedbackAboveUniversalFooter: attachAboveFooter,
			makeFeedbackSticky: makeSticky,
			hasUserDisabledFeedbackStickiness: () => userDisabled,
			hasUserSeenOneFourthOfArticle: hasSeenOneFourth,
			updateFeedbackStickiness: updateStickiness,
			isUniversalFooterVisible: isFooterInViewport,
			setExtendedFeedbackHeight,
			userHasDisabledFeedbackStickinessCookieName: cookieName,
			setUserHasDisabledFeedbackStickiness: (v: boolean) => { userDisabled = v; },
			setUniversalFooterInViewport: (v: boolean) => { footerInViewport = v; },
			setWindowTooTightForStickiness: (v: boolean) => { windowTooTight = v; },
			setArticleTooShortForStickiness: (v: boolean) => { articleTooShort = v; },
			setUserHasSeenOneFourthOfArticle: (v: boolean) => { seenOneFourth = v; },
		};
	})();

	occe.Controls.Feedback.initializeControl();
}
